import React from 'react'

import { i18n, useTheme } from '@thg-commerce/enterprise-core'
import { HorizontalAlignment } from '@thg-commerce/enterprise-widget-accordion-container'
import { GridItem } from '@thg-commerce/gravity-system'
import { AccordionBorderPlacement } from '@thg-commerce/gravity-system/components/Accordion'
import { BundleCategory } from '@thg-commerce/widget-build-your-own-bundle'

import { ProductCategoryContent } from './ResponsiveBuildYourOwnBundleProductsCategory'
import {
  CategoryItemSubtitle,
  IconAndTextContainer,
  SelectedProductsText,
  StyledAccordionContainer,
  StyledGrid,
  StyledSuccessIcon,
} from './styles'

export interface BundleProduct {
  price: number
  categoryId: string
  quantity: number
  sku: number
}

export interface ResponsiveBuildYourOwnBundleProductsProps {
  expandAllCategories?: boolean
  unlimitedProducts?: boolean
  focusedAccordionItem: number | null
  className?: string
  selectedBundleProducts: BundleProduct[]
  setSelectedBundleProducts: (
    callback: (currentProducts: BundleProduct[]) => BundleProduct[],
  ) => void
  banners: BundleCategory[]
  availableBundleQuantitySelection: number
}

interface ProductAccordionSubtitleProps {
  selectedProductCount: number
  categoryMaxProducts: number
  isBundleFull: boolean
}

export const ProductAccordionSubtitle = (
  props: ProductAccordionSubtitleProps,
) => {
  const i18nText = {
    itemsSelectedText: i18n('widget.buildyourbundle.selected.text'),
    numberOfItemsText: i18n(
      'widget.buildyourbundle.oftext.text',
      `${props.categoryMaxProducts}`,
    ),
    chooseItemsText: i18n('widget.buildyourbundle.chooseitems.text'),
  }
  return props.selectedProductCount ? (
    <IconAndTextContainer>
      {(props.selectedProductCount === props.categoryMaxProducts ||
        props.isBundleFull) && <StyledSuccessIcon />}
      <SelectedProductsText
        maxProductsReached={
          props.selectedProductCount === props.categoryMaxProducts ||
          props.isBundleFull
        }
      >
        {props.selectedProductCount}{' '}
        {props.categoryMaxProducts && i18nText.numberOfItemsText}{' '}
        {i18nText.itemsSelectedText}
      </SelectedProductsText>
    </IconAndTextContainer>
  ) : (
    <CategoryItemSubtitle>{i18nText.chooseItemsText}</CategoryItemSubtitle>
  )
}

export const ResponsiveBuildYourOwnBundleProducts = (
  props: ResponsiveBuildYourOwnBundleProductsProps,
) => {
  const focusRefs = props.banners?.map(() =>
    React.useRef<HTMLDivElement | null>(null),
  )

  const theme = useTheme()

  React.useEffect(() => {
    if (props.focusedAccordionItem !== null) {
      focusRefs[props.focusedAccordionItem].current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [props.focusedAccordionItem])

  return (
    <StyledGrid>
      <GridItem colSpan={12}>
        <StyledAccordionContainer
          items={props.banners.map((category, index) => {
            const countSelectedProductsPerCategory = props.selectedBundleProducts
              .filter((product) => product.categoryId === category.id)
              .reduce((acc, product) => {
                return acc + product.quantity
              }, 0)

            return {
              key: category.id,
              textStyle:
                theme.widget.buildYourOwnBundle.components.accordion.title.font,
              title: category.categoryTitle,
              subtitleElement: (
                <ProductAccordionSubtitle
                  selectedProductCount={countSelectedProductsPerCategory}
                  categoryMaxProducts={category.maxProducts}
                  isBundleFull={props.availableBundleQuantitySelection === 0}
                />
              ),
              elementContent: (
                <ProductCategoryContent
                  productCategory={category}
                  setSelectedBundleProducts={props.setSelectedBundleProducts}
                  selectedBundleProducts={props.selectedBundleProducts}
                  numberOfProductsInSelectedCategory={
                    countSelectedProductsPerCategory
                  }
                  enableQuantitySelector={category.enableQuantitySelector}
                  availableBundleQuantitySelection={
                    props.availableBundleQuantitySelection
                  }
                />
              ),
              isOpen: !props.expandAllCategories ? index === 0 : true,
              focusRef: focusRefs[index],
              borderPlacement: AccordionBorderPlacement.TOP,
            }
          })}
          alignment={HorizontalAlignment.LEFT}
        />
      </GridItem>
    </StyledGrid>
  )
}
